import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild, ChangeDetectorRef, inject } from '@angular/core';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { MatPaginator } from '@angular/material/paginator';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription, Observable, firstValueFrom, interval } from 'rxjs';
import { takeUntil, map, shareReplay, startWith, take, takeWhile, switchMap, tap } from 'rxjs/operators';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { LegalizationStatusEnum } from 'src/app/models/enums/legalization-status-enum';
import { LegalizationFileDto } from 'src/app/models/legalization-file-dto';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { LegalizationFilesModalComponent, LegalizationFilesModalData } from '../legalization-files-modal/legalization-files-modal.component';
import { UntypedFormGroup } from '@angular/forms';
import { Filter } from 'src/app/models/common/filter';
import { ScreenService } from 'src/app/core/screen.service';
import { AddLegalizationInformationModalData } from '../save-info-modals/AddLegalizationInformationModalData';
import { SaveLegalizationInfoByCoordinatorComponent } from '../save-info-modals/save-legalization-info-by-coordinator/save-legalization-info-by-coordinator.component';
import { SaveLegalizationInfoByLegalizerModalComponent } from '../save-info-modals/save-legalization-info-by-legalizer/save-legalization-info-by-legalizer-modal.component';
import { LegalizationDetailsComponent } from '../legalization-details/legalization-details/legalization-details.component';
import { SplitComponentService } from 'src/app/split/services/split-component.service';
import { SplitComponentEnum } from 'src/app/common/enums/split-components';
import {
  EditLegalizationDocumentModalComponent,
  EditLegalizationDocumentModalData,
} from '../save-info-modals/components/edit-legalization-document/edit-legalization-document-modal.component';
import { AssignLegalizationDocumentComponent } from '../assign-legalization-document/assign-legalization-document.component';
import { AssignLegalizationDocumentData } from '../save-info-modals/AssignLegalizationDocumentData';
import { LegalizationsListGridDataSource } from './legalization-list-grid.datasource';
import { LegalizationService } from 'src/app/data/legalization.service';
import { LegalizationDocumentStatusEnum } from 'src/app/models/enums/legalization-document-status-enum';
import { LegalizationAlertStatusEnum } from 'src/app/models/enums/legalization-alert-status-enum';
import { AllLegalizationGridDto } from '../../models/dtos/all-legalization-grid-dto';
import { FilterService } from '../../list-filter/services/filter.service';
import { LegalizationListFilterService } from '../services/legalization-list-filter-service';
import { LegalizationStatusHelperService } from '../services/legalization-status-helper-service';
import { SignalRService } from '../../data/signalr.service';
import { AuthService } from '../../core/authentication/auth.service';

const LEGALIZATION_STATUS_TRANSLATION_PREFIX = 'Legalization';
const REGISTER_PREFIX = 'Register';
const SignalRGroupName = 'LegalizationGroup';

@Component({
  selector: 'app-legalization-list',
  templateUrl: './legalization-list.component.html',
  styleUrls: ['./legalization-list.component.scss'],
  providers: [{ provide: FilterService, useClass: LegalizationListFilterService }],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
    trigger('rowExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  standalone: false
})
export class LegalizationListComponent implements OnInit, AfterViewInit, OnDestroy {
  screenService = inject(ScreenService);
  private translateService = inject(TranslateService);
  private dialog = inject(MatDialog);
  private splitComponentService = inject(SplitComponentService);
  private legalizationService = inject(LegalizationService);
  private filterService = inject(FilterService);
  private cdr = inject(ChangeDetectorRef);
  private statusHelperService = inject(LegalizationStatusHelperService);
  private signalRService = inject(SignalRService);
  private authService = inject(AuthService);

  @ViewChild(CdkVirtualScrollViewport, { static: true }) viewport: CdkVirtualScrollViewport;
  @ViewChild('tableContainer', { static: true }) tableContainerRef: ElementRef;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  displayedColumns = [
    'lastName',
    'firstName',
    'startDate',
    'endDate',
    'statusName',
    'expandingIcon',
    'details',
    'documentTypeName',
    'documentStartDate',
    'documentEndDate',
    'documentStatusName',
    'alert',
    'actions',
  ];
  displayedExpandedColumns = [
    'expandingIcon',
    'details',
    'documentTypeName',
    'documentStartDate',
    'documentEndDate',
    'documentStatusName',
    'alert',
    'actions',
  ];
  expansionDataName = 'CorrectedDocuments';
  filtersFormGroup: UntypedFormGroup;
  expandedElementLegalizationId?: number;
  expandedElementDocumentId?: number;
  activeLegalizationId?: number;
  activeDocumentId?: number;
  areFiltersExpanded: boolean = false;
  splitComponentId = 'legalization-list-split-component';
  correctDocumentLabel: string;
  addDocumentLabel: string;
  legalizationChangesSubscription: Subscription | undefined;
  hubName: string = 'LegalizationHub';
  workerId: number | undefined;
  retryConnectionDelay: number = 5000;
  retryConnectionCount: number = 120;

  isExpandedRow = (index: number, row: any) => row.DocumentId === this.expandedElementDocumentId;
  isStandardRow = (index: number, row: any) => true;

  private subject = new Subject<void>();
  private readonly unsubscribe$ = new Subject<void>();
  private readonly reconnectCancelation$ = new Subject<void>();
  private isDestroyed = false;

  public readonly registerPrefix = `${LEGALIZATION_STATUS_TRANSLATION_PREFIX}.${REGISTER_PREFIX}`;

  public filters: Filter[] = [];
  private loadingSubscription: Subscription;

  sticky = false;
  height = 0;
  page = 1;
  actualPageStart = 1;
  pageSize = 40;
  offset$: Observable<number>;
  isLoading = false;
  sortingField: string = 'CreationDate';
  sortingDirection: string = 'desc';

  private lastLegalizationId: number | null = null;
  private isCurrentGroupOdd: boolean = true;

  public get scrollItemPosition(): number {
    return this.viewport?.getRenderedRange().end || 0;
  }

  public readonly legalizationStatusEnum = LegalizationStatusEnum;
  public readonly legalizationDocumentStatusEnum = LegalizationDocumentStatusEnum;
  public readonly legalizationAlertStatusEnum = LegalizationAlertStatusEnum;
  public dataSource: LegalizationsListGridDataSource;

  constructor() { }

  async ngOnInit(): Promise<void> {
    this.initDataSource();
    this.height = this.tableContainerRef.nativeElement.clientHeight || 500;
    this.expandedElementDocumentId = undefined;

    this.filterService.filters$.pipe(takeUntil(this.unsubscribe$)).subscribe((filters) => {
      this.filterData(filters);
      this.cdr.detectChanges();
    });

    this.dataSource.data$.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.calculateRowColors();
    });

    this.workerId = await firstValueFrom(this.authService.getWorkerIdByAuthServerUserId());

    if (this.workerId) {
      this.startSignalRConnection();
    }

    this.updateLabels();
    this.fetchLegalizations(1);
  }

  ngAfterViewInit(): void {
    this.translateService.onLangChange.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: () => {
        this.resetData();
        this.updateLabels();
        this.fetchLegalizations();
      },
    });
  }

  async resetFilters(): Promise<void> {
    await firstValueFrom(this.filterService.resetFilters());
  }

  toggleFiltersPanel = () => (this.areFiltersExpanded = !this.areFiltersExpanded);

  expandRow(expandedElementId, documentId) {
    this.expandedElementLegalizationId = expandedElementId === this.expandedElementLegalizationId ? null : expandedElementId;
  }

  expandDocument(documentId: number) {
    if (!documentId) {
      return;
    }

    const element = this.dataSource.allData.find((item) => item.DocumentId === documentId);
    if (!element || !element.CorrectedDocuments || element.CorrectedDocuments.length === 0) {
      return;
    }

    this.expandedElementDocumentId = this.expandedElementDocumentId === documentId ? undefined : documentId;
  }

  hasCorrectedDocuments = (index: number, row: any) => {
    return row && row.CorrectedDocuments && row.CorrectedDocuments.length > 0;
  };

  public openLegalizationFilesModal(legalizationFiles: LegalizationFileDto[], workerId: number, documentTypeName: string): void {
    const dialogRef = this.dialog.open(
      LegalizationFilesModalComponent,
      this.dialogConfig(<LegalizationFilesModalData>{
        LegalizationFiles: legalizationFiles,
        WorkerId: workerId,
        DocumentTypeName: documentTypeName,
      }),
    );

    dialogRef.afterClosed().subscribe((result) => this.handleDialogResponse(result));
  }

  public openDocumentLegalizationForCoordinator(workerId: number, firstName?: string, lastName?: string): void {
    this.dialog.open(
      SaveLegalizationInfoByCoordinatorComponent,
      this.dialogConfig(<AddLegalizationInformationModalData>{
        Title: 'Legalization.Title',
        WorkerId: workerId,
        FirstName: firstName,
        LastName: lastName,
      }),
    );
  }

  public openDocumentLegalizationForLegalizer(element: AllLegalizationGridDto): void {
    let isCorrection = this.isToCorrection(element);

    this.dialog.open(
      SaveLegalizationInfoByLegalizerModalComponent,
      this.dialogConfig(<AddLegalizationInformationModalData>{
        Title: 'Legalization.Title',
        WorkerId: element.WorkerId,
        LegalizationDocumentId: isCorrection ? element.DocumentId : null,
        IsCorrection: isCorrection,
        PreSelectedLegalizationsIds: [element.LegalizationId],
      }),
    );
  }

  public openAssigningDocuments(workerId: number, firstName: string, lastName: string, legalizationDocumentId: number): void {
    const dialogRef = this.dialog.open(
      AssignLegalizationDocumentComponent,
      this.dialogConfig(<AssignLegalizationDocumentData>{
        Title: this.translateService.instant('Legalization.AssignDocuments'),
        FirstName: firstName,
        LastName: lastName,
        WorkerId: workerId,
        LegalizationDocumentId: legalizationDocumentId,
      }),
    );

    dialogRef.afterClosed().subscribe((result) => this.handleDialogResponseWithRefresh(result));
  }

  public async editDocument(legalizationDocumentId: number): Promise<void> {
    const dialogRef = this.dialog.open(
      EditLegalizationDocumentModalComponent,
      this.dialogConfig(<EditLegalizationDocumentModalData>{
        LegalizationDocumentId: legalizationDocumentId,
      }),
    );

    const result = await firstValueFrom(dialogRef.afterClosed());

    if (result && result.isSuccess) {
      if (result.openAssignDocuments && result.workerData) {
        const assignDialogRef = this.dialog.open(
          AssignLegalizationDocumentComponent,
          this.dialogConfig(<AssignLegalizationDocumentData>{
            Title: this.translateService.instant('Legalization.AssignDocuments'),
            WorkerId: result.workerData.workerId,
            LegalizationDocumentId: result.workerData.legalizationDocumentId,
          }),
        );
      }
    }
  }

  filterData(filters: Filter[]) {
    this.filters = filters;
    this.resetData();
    this.fetchLegalizations(1);
  }

  hasLegalizationStatus(legalizationStatusEnum: LegalizationStatusEnum, legalizationStatusId: number): boolean {
    return legalizationStatusId === legalizationStatusEnum;
  }

  showDetails(id: number, documentId?: number): void {
    this.activeLegalizationId = id;
    this.activeDocumentId = documentId;
    this.splitComponentService.addComponent(this.splitComponentId, {
      id: SplitComponentEnum.LegalizationDetails,
      componentType: LegalizationDetailsComponent,
      direction: 'horizontal',
      data: {
        id: id,
        documentId: documentId,
        splitId: this.splitComponentId,
      },
      size: 33,
      header: {
        closeButton: true,
      },
    });
  }

  private handleDialogResponse(result: any): void {
    if (!result) {
      return;
    }

    if (result.isSuccess) {
      this.subject.next();
    }
  }

  private handleDialogResponseWithRefresh(result: any): void {
    if (!result) {
      return;
    }

    if (result.isSuccess) {
      this.subject.next();
    }

    this.filterData(this.filters);
  }

  async nextBatch(index: number) {
    if (!this.sticky) {
      this.sticky = true;
    }

    const buffer = 20;
    const range = this.viewport.getRenderedRange();
    const end = range.end + 1;
    const dataLength = this.dataSource.allData.length;

    const loadThreshold = 0.75;
    const scrollPercentage = end / dataLength;

    if (
      !this.isLoading &&
      scrollPercentage > loadThreshold &&
      end + buffer >= dataLength &&
      dataLength < (this.dataSource.absoluteDataLength || Infinity)
    ) {
      const nextPage = this.page + 1;
      this.fetchLegalizations(nextPage);
    }
  }

  private fetchLegalizations(page?: number, pageSize?: number): void {
    const pageToFetch = page || this.page;
    const size = pageSize ?? this.pageSize;

    if (this.isLoading) {
      return;
    }

    this.isLoading = true;

    if (pageToFetch === 1 && size > this.pageSize) {
      this.page = Math.ceil(size / this.pageSize);
    } else if (pageToFetch > this.page) {
      this.page = pageToFetch;
    }

    this.dataSource.legalizationsSubject.next({
      Page: pageToFetch,
      PageSize: size,
      Filters: this.filters,
      SortingField: this.sortingField,
      SortingDirection: this.sortingDirection,
    });

    this.dataSource.isLoading$.pipe(takeUntil(this.unsubscribe$)).subscribe((isLoading) => {
      this.isLoading = isLoading;
    });
  }

  isFirstOccurrence(element: AllLegalizationGridDto): boolean {
    if (!element || !element.LegalizationId) {
      return true;
    }

    const visibleData = this.dataSource.allData;
    const currentIndex = visibleData.findIndex((item) => item.LegalizationId === element.LegalizationId && item.DocumentId === element.DocumentId);

    if (currentIndex <= 0) {
      return true;
    }

    const previousItem = visibleData[currentIndex - 1];
    return previousItem.LegalizationId !== element.LegalizationId;
  }

  isLastOccurrence(element: AllLegalizationGridDto): boolean {
    if (!element || !element.LegalizationId) {
      return true;
    }

    const visibleData = this.dataSource.allData;
    const currentIndex = visibleData.findIndex((item) => item.LegalizationId === element.LegalizationId && item.DocumentId === element.DocumentId);

    if (currentIndex === -1 || currentIndex >= visibleData.length - 1) {
      return true;
    }

    const nextItem = visibleData[currentIndex + 1];
    return nextItem.LegalizationId !== element.LegalizationId;
  }

  trackBy(index: number, item: AllLegalizationGridDto): string {
    return `${item.LegalizationId}-${item.DocumentId || ''}`;
  }

  private dialogConfig = <Type>(data: Type): MatDialogConfig<Type> => {
    const dialogConfig = new MatDialogConfig<Type>();
    dialogConfig.panelClass = 'legalization-form-dialog';
    dialogConfig.data = data;
    return dialogConfig;
  };

  private initDataSource(): void {
    this.dataSource = new LegalizationsListGridDataSource(this.legalizationService, { viewport: this.viewport });

    this.offset$ = this.viewport.renderedRangeStream.pipe(map(() => -(this.viewport?.getOffsetToRenderedContentStart() ?? 0)));
  }

  private resetData(): void {
    this.page = 1;
    this.dataSource.reset();
    this.lastLegalizationId = null;
    this.isCurrentGroupOdd = true;
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.reconnectCancelation$.next();
    this.reconnectCancelation$.complete();

    if (this.loadingSubscription) {
      this.loadingSubscription.unsubscribe();
    }

    if (this.legalizationChangesSubscription) {
      this.legalizationChangesSubscription.unsubscribe();
    }

    this.signalRService.closeConnection(this.hubName);
  }

  public getStatusClass(legalizationStatusId: number): string {
    return this.statusHelperService.getStatusClass(legalizationStatusId);
  }

  public getDocumentStatusClass(documentStatusId: number): string {
    return this.statusHelperService.getDocumentStatusClass(documentStatusId);
  }

  public getAlertStatusClass(alertStatusId: number): string {
    return this.statusHelperService.getAlertStatusClass(alertStatusId);
  }

  isOddLegalization(element: AllLegalizationGridDto & { isOdd?: boolean }): boolean {
    return element.isOdd;
  }

  isEvenLegalization(element: AllLegalizationGridDto & { isOdd?: boolean }): boolean {
    return !element.isOdd;
  }

  existsDocumentButtonVisible(element: AllLegalizationGridDto): boolean {
    return element.DocumentId !== null;
  }

  isToCorrection(element: AllLegalizationGridDto): boolean {
    return element.DocumentStatusId === LegalizationDocumentStatusEnum.RequiresCorrection;
  }

  addOrCorrectLabel(element: AllLegalizationGridDto): string {
    return this.isToCorrection(element) ? this.correctDocumentLabel : this.addDocumentLabel;
  }

  private startSignalRConnection(): Observable<void> {
    if (this.legalizationChangesSubscription) {
      this.legalizationChangesSubscription.unsubscribe();
      this.legalizationChangesSubscription = undefined;
    }

    const connected$ = this.signalRService
      .startConnection(this.hubName, SignalRGroupName, {
        onclose: async () => {
          if (!this.isDestroyed) {
            await this.reconnect();
          }
        }
      })
      .pipe(shareReplay());

    this.legalizationChangesSubscription = connected$
      .pipe(switchMap(() => this.signalRService.receiveLeglizationUpdate()))
      .subscribe((update: string) => this.legalizationsUpdated(update));

    return connected$;
  }

  private legalizationsUpdated(log: string): void {
    this.dataSource.setAllPagesAsObsolete();
    const { page, pageSize } = this.getActualPage();
    this.fetchLegalizations(page, pageSize);
  }

  private async reconnect(): Promise<void> {
    if (this.isDestroyed) {
      return;
    }

    await firstValueFrom(
      interval(this.retryConnectionDelay)
        .pipe(startWith(0))
        .pipe(map(() => this.startSignalRConnection()))
        .pipe(takeWhile(() => this.signalRService.isConnected(this.hubName)))
        .pipe(takeUntil(this.reconnectCancelation$))
        .pipe(take(this.retryConnectionCount)));
  }

  private updateLabels() {
    this.correctDocumentLabel = this.translateService.instant(`${this.registerPrefix}.CorrectDocument`);
    this.addDocumentLabel = this.translateService.instant(`${this.registerPrefix}.AddDocument`);
  }

  private getActualPage() {
    const range = this.viewport.getRenderedRange();
    const end = range.end;

    const pageEnd = Math.floor((end - 1) / this.pageSize) + 1;

    const pageSize = this.pageSize * pageEnd;
    const page = 1;

    return { page, pageSize };
  }

  private calculateRowColors(): void {
    this.lastLegalizationId = null;
    this.isCurrentGroupOdd = true;
    this.dataSource.allData.forEach((row: AllLegalizationGridDto & { isOdd?: boolean }) => {
      if (row.LegalizationId !== this.lastLegalizationId) {
        this.isCurrentGroupOdd = !this.isCurrentGroupOdd;
        this.lastLegalizationId = row.LegalizationId;
      }
      row.isOdd = this.isCurrentGroupOdd;
    });
  }
}
